/**
 * Weapon (projectile) models: every weapon on the ordnance kit (fuselage,
 * ogive/blunt/cone noses, swept fins, flared nozzles, hazard bands, sensor
 * ports) with its own payload signature. Design scale (×3 game), +Z forward,
 * nozzle exits near z ≈ −0.3…−0.45 so the existing exhaust configs line up.
 *
 * Exhaust, health packs and PROJECTILE_SCALE live in projectileModels.ts.
 * Design reference: design_sheets/projectiles_v2.html
 */

import * as THREE from 'three';
import type { WeaponKey } from '../types';
import { WEAPONS } from '../constants';
import { ORD, tint, mat, matE, at, group, tube, ring, fuselage, noseOgive, noseCone, noseBlunt, nozzle, finSet, hazardBand, port, dish, cage } from './ordnanceKit';

type Builder = (c: string) => THREE.Group;

// 1 — STANDARD · Terrain Probe: clean survey round
const standard: Builder = c => group(
  fuselage(0.16, 0.5, c, -0.05),
  noseOgive(0.16, 0.33, tint(c, 0.2).getStyle(), 0.2),
  ring(0.165, 0.012, matE(tint(c, 0.5), 0.6), 0.14, 14),          // sensor band
  hazardBand(0.16, -0.14, 0.05, c),
  finSet(4, 0.16, -0.3, 0.2, 0.08, 0.12, 0.08, tint(c, -0.2).getStyle(), Math.PI / 4),
  nozzle(0.08, 0.12, 0.1, -0.4),
  port(0.165, 0.0, Math.PI / 2, tint(c, 0.6).getStyle(), 0.02),
);

// 2 — HEAVY · Deep Core Sample: fat, braced, blunt
const heavy: Builder = c => {
  const g = group(fuselage(0.24, 0.45, c, 0), noseBlunt(0.24, tint(c, 0.15).getStyle(), 0.225));
  for (const z of [-0.14, 0.0, 0.14]) g.add(ring(0.245, 0.016, mat(tint(c, -0.3)), z, 16));   // reinforcement
  g.add(hazardBand(0.24, 0.08, 0.05, c), finSet(4, 0.24, -0.22, 0.16, 0.08, 0.1, 0.06, ORD.darkMetal));
  g.add(nozzle(0.14, 0.2, 0.12, -0.34), port(0.245, 0.04, Math.PI / 2, tint(c, 0.6).getStyle()));
  return g;
};

// 3 — CLUSTER · Multi-Point Survey Array: carrier with six sub-munitions in collars
const cluster: Builder = c => {
  const g = group(fuselage(0.12, 0.5, c, -0.02), noseCone(0.12, 0.26, tint(c, 0.2).getStyle(), 0.23));
  const subColor = tint(c, 0.35).getStyle();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const sub = group(tube(0.045, 0.045, 0.22, mat(subColor), 0.0, 8), noseCone(0.045, 0.08, tint(c, 0.5).getStyle(), 0.11, 8), at(new THREE.Mesh(new THREE.CircleGeometry(0.04, 8), mat(ORD.throat)), 0, 0, -0.111, Math.PI));
    sub.position.set(Math.cos(a) * 0.19, Math.sin(a) * 0.19, 0.0);
    g.add(sub);
  }
  g.add(ring(0.19, 0.014, mat(ORD.darkMetal), 0.08, 18), ring(0.19, 0.014, mat(ORD.darkMetal), -0.08, 18));   // clamp collars
  g.add(finSet(4, 0.12, -0.27, 0.16, 0.07, 0.1, 0.06, tint(c, -0.2).getStyle(), Math.PI / 4), nozzle(0.06, 0.1, 0.08, -0.3));
  g.add(port(0.12, 0.16, Math.PI / 2, tint(c, 0.6).getStyle(), 0.02));   // dispersal sensor
  return g;
};

// 4 — NUKE · Comprehensive Geological Assessment: the bulbous one, box tail with a stabilizer ring
const nuke: Builder = c => {
  const body = at(new THREE.Mesh(new THREE.SphereGeometry(0.3, 16, 12), mat(c)), 0, 0, 0.0); body.scale.set(1, 1, 1.5);
  const g = group(body);
  g.add(ring(0.3, 0.014, mat(tint(c, -0.3)), 0.0, 20), hazardBand(0.26, 0.25, 0.06, '#e8c040'), hazardBand(0.26, -0.25, 0.06, '#e8c040'));
  g.add(tube(0.14, 0.18, 0.2, mat(tint(c, -0.15)), -0.5, 14));                                 // tail cone
  g.add(finSet(4, 0.18, -0.6, 0.22, 0.14, 0.2, 0.0, ORD.darkMetal), ring(0.36, 0.014, mat(ORD.darkMetal), -0.52, 24));   // box-fin ring
  for (let i = 0; i < 4; i++) g.add(at(new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.2, 6), mat(ORD.darkMetal)), Math.cos(i * Math.PI / 2 + Math.PI / 4) * 0.36, Math.sin(i * Math.PI / 2 + Math.PI / 4) * 0.36, -0.52, Math.PI / 2));
  g.add(nozzle(0.08, 0.12, 0.08, -0.62));
  for (let i = 0; i < 3; i++) g.add(port(0.3, 0.1, i * 2.1 + 0.4, '#ff9a40', 0.03));
  return g;
};

// 5 — PROBE · Ecological Recon Drone: small, dish on top, sensor panel, solar wings
const probe: Builder = c => {
  const g = group(fuselage(0.11, 0.4, c, 0.0), noseCone(0.11, 0.2, tint(c, 0.25).getStyle(), 0.2));
  g.add(at(dish(0.1, tint(c, 0.5).getStyle()), 0, 0.17, 0.02, -0.5));
  g.add(at(new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.1, 0.16, 1, 1, 1), mat(ORD.glass)), 0.12, 0, -0.02));           // AESA panel
  for (const sx of [-1, 1]) g.add(at(new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.01, 0.12), mat(ORD.solar)), sx * 0.24, -0.02, -0.08), at(new THREE.Mesh(new THREE.BoxGeometry(0.27, 0.016, 0.02), mat(ORD.lightMetal)), sx * 0.24, -0.02, -0.08));
  g.add(port(0.11, 0.1, -Math.PI / 2, tint(c, 0.6).getStyle(), 0.02), nozzle(0.04, 0.07, 0.07, -0.26));
  return g;
};

// 6 — PUNCH · Direct Assessment Tool: long penetrator dart with a sabot
const punch: Builder = c => group(
  tube(0.055, 0.065, 0.9, mat(ORD.lightMetal), -0.1, 10),
  noseCone(0.055, 0.35, tint(c, 0.3).getStyle(), 0.35, 10),
  ring(0.07, 0.01, mat(c), -0.0, 12), ring(0.07, 0.01, mat(c), -0.3, 12),
  finSet(4, 0.065, -0.5, 0.18, 0.04, 0.09, 0.1, c, Math.PI / 4),
  tube(0.11, 0.12, 0.12, mat(tint(c, -0.3)), -0.42, 12),                                      // sabot collar
  nozzle(0.04, 0.06, 0.06, -0.52),
  port(0.06, 0.15, Math.PI / 2, tint(c, 0.7).getStyle(), 0.015),
);

// 7 — ANTIGRAV · Gravitational Displacement Device: sleek body threaded through glowing rings
const antigrav: Builder = c => {
  const g = group(fuselage(0.13, 0.5, c, -0.02), noseOgive(0.13, 0.3, tint(c, 0.25).getStyle(), 0.23));
  for (const z of [0.1, -0.05, -0.2]) g.add(ring(0.2, 0.02, matE(tint(c, 0.4), 0.6), z, 20));
  for (const z of [0.1, -0.05, -0.2]) for (let i = 0; i < 3; i++) g.add(at(new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.07, 0.02), mat(ORD.darkMetal)), Math.cos(i * 2.094) * 0.165, Math.sin(i * 2.094) * 0.165, z, 0, 0, i * 2.094));
  g.add(finSet(3, 0.13, -0.28, 0.14, 0.06, 0.08, 0.05, tint(c, -0.2).getStyle(), Math.PI / 2), nozzle(0.06, 0.1, 0.08, -0.36));
  return g;
};

// 8 — GRAVITY BOMB · Localized Mass Anomaly: fuselage pushing a caged dark sphere
const gravBomb: Builder = c => {
  const g = group(fuselage(0.15, 0.32, c, -0.14));
  g.add(cage(0.2, 0.3, 6, ORD.lightMetal, 0.17), ring(0.2, 0.012, mat(ORD.lightMetal), 0.17, 18));
  g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.15, 16, 12), matE(tint(c, -0.4), 0.9)), 0, 0, 0.17));   // the anomaly
  g.add(ring(0.155, 0.01, matE(tint(c, 0.5), 0.8), 0.17, 20));
  g.add(finSet(4, 0.15, -0.3, 0.14, 0.06, 0.1, 0.06, tint(c, -0.2).getStyle(), Math.PI / 4), nozzle(0.07, 0.11, 0.08, -0.36));
  return g;
};

// 9 — TETHER MINE · Kinetic Restraint Payload: disc hub with four emitter arms
const tether: Builder = c => {
  const g = group(tube(0.22, 0.22, 0.1, mat(c), 0.0, 20), ring(0.22, 0.012, mat(tint(c, -0.3)), 0.05, 24), ring(0.22, 0.012, mat(tint(c, -0.3)), -0.05, 24));
  g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.1, 16), matE(tint(c, 0.5), 0.8)), 0, 0, 0.051));           // lens
  for (let i = 0; i < 4; i++) {
    const a = i * Math.PI / 2 + Math.PI / 4;
    const arm = group(at(new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.04, 0.22), mat(ORD.darkMetal)), 0, 0, 0), at(new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 6), matE(tint(c, 0.6), 0.9)), 0, 0, 0.12));
    arm.position.set(Math.cos(a) * 0.26, Math.sin(a) * 0.26, 0.02);
    arm.rotation.z = a;
    g.add(arm);
  }
  g.add(tube(0.08, 0.1, 0.12, mat(tint(c, -0.15)), -0.11, 12), nozzle(0.05, 0.08, 0.06, -0.2));
  return g;
};

// 10 — TERRAFORMER · Geological Restoration Charge: fuselage carrying an organic seed pod
const terraformer: Builder = c => {
  const g = group(fuselage(0.13, 0.4, c, -0.1));
  const podColor = '#6fa868';
  g.add(noseOgive(0.17, 0.36, podColor, 0.1));
  for (let i = 0; i < 4; i++) { const a = i * Math.PI / 2; const leaf = at(new THREE.Mesh(new THREE.SphereGeometry(0.11, 10, 6), mat('#4e8a52')), Math.cos(a) * 0.14, Math.sin(a) * 0.14, 0.12, 0, 0, a); leaf.scale.set(0.35, 1, 1.6); g.add(leaf); }
  g.add(ring(0.17, 0.012, mat(tint(podColor, -0.3)), 0.1, 16), ring(0.135, 0.012, mat(tint(c, -0.3)), -0.02, 14));
  for (let i = 0; i < 3; i++) g.add(port(0.135, -0.15, i * 2.094, '#b8e890', 0.02));
  g.add(finSet(3, 0.13, -0.3, 0.14, 0.06, 0.09, 0.05, tint(c, -0.2).getStyle(), Math.PI / 2), nozzle(0.06, 0.1, 0.08, -0.36));
  return g;
};

// 11 — EMP · Electromagnetic Survey Disruptor: capacitor rings and an open emitter horn
const emp: Builder = c => {
  const g = group(fuselage(0.14, 0.42, c, -0.08));
  for (const z of [0.0, -0.1, -0.2]) g.add(ring(0.17, 0.02, mat(ORD.lightMetal), z, 18));
  g.add(tube(0.2, 0.1, 0.18, mat(tint(c, -0.2)), 0.22, 16));                                   // horn (open toward +Z)
  g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.1, 16), matE(tint(c, 0.6), 0.9)), 0, 0, 0.135));   // emitter glow
  g.add(at(new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.12, 8), matE(tint(c, 0.5), 0.6)), 0, 0, 0.26, Math.PI / 2));   // tip
  g.add(finSet(4, 0.14, -0.26, 0.14, 0.06, 0.09, 0.05, tint(c, -0.25).getStyle(), Math.PI / 4), nozzle(0.06, 0.1, 0.08, -0.34));
  return g;
};

// 12 — DRILL · Sub-Surface Penetrator: heavy body, stepped drill tip with helical ridges
const drill: Builder = c => {
  const g = group(fuselage(0.2, 0.4, c, -0.1), hazardBand(0.2, -0.22, 0.06, '#e8c040'));
  const drillColor = ORD.lightMetal;
  g.add(tube(0.14, 0.2, 0.1, mat(tint(c, -0.2)), 0.15, 14));
  g.add(noseCone(0.14, 0.42, drillColor, 0.2, 12));
  for (let i = 0; i < 4; i++) { const z = 0.24 + i * 0.08, r = 0.14 * (1 - (z - 0.2) / 0.42) + 0.01; g.add(ring(r, 0.012, mat(ORD.darkMetal), z, 14)); }   // helical-ish ridges
  g.add(finSet(4, 0.2, -0.3, 0.16, 0.08, 0.1, 0.06, ORD.darkMetal), nozzle(0.1, 0.16, 0.1, -0.4));
  for (let i = 0; i < 2; i++) g.add(port(0.2, -0.05, i * Math.PI + Math.PI / 2, tint(c, 0.6).getStyle()));
  return g;
};

// 13 — FIRE · Incendiary Remediation Device: rounded warhead with vent cluster, flame bands
const fire: Builder = c => {
  const g = group(fuselage(0.16, 0.45, c, -0.05), noseBlunt(0.16, tint(c, 0.2).getStyle(), 0.175));
  for (let i = 0; i < 4; i++) { const a = i * Math.PI / 2 + Math.PI / 4; g.add(at(new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.1, 8), mat(ORD.darkMetal)), Math.cos(a) * 0.1, Math.sin(a) * 0.1, 0.3, Math.PI / 2)); g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.026, 8), matE('#ffb040', 0.9)), Math.cos(a) * 0.1, Math.sin(a) * 0.1, 0.351)); }
  g.add(ring(0.165, 0.014, mat('#ffb040'), 0.05, 16), ring(0.165, 0.014, mat('#ff6a20'), -0.02, 16), ring(0.165, 0.014, mat('#ffb040'), -0.09, 16));
  g.add(finSet(4, 0.16, -0.28, 0.16, 0.08, 0.1, 0.06, tint(c, -0.3).getStyle(), Math.PI / 4), nozzle(0.08, 0.13, 0.1, -0.4));
  g.add(port(0.165, -0.18, Math.PI / 2, '#ffb040', 0.02));
  return g;
};

const BUILDERS: Record<WeaponKey, Builder> = {
  standard, heavy, cluster, nuke, probe, punch, antigrav, gravBomb, tether, terraformer, emp, drill, fire,
  mortar: heavy,   // S4 stand-in: the field tube borrows the heavy launcher's model until the style sheet
};

export function buildWeaponModel(weaponKey: WeaponKey): THREE.Group {
  return BUILDERS[weaponKey](WEAPONS[weaponKey].color);
}
